import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { mergeProps } from '@react-aria/utils';
import { useButton } from '@react-aria/button';
import { useFocusRing } from '@react-aria/focus';
import { useSelect } from '@react-aria/select';
import { useSelectState } from '@react-stately/select';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { ZIcon } from '@zanichelli/albe-web-components/react';

import ListBox from './listbox';
import Popover from './popover';

// TODO: valore di default configurabile da prop

export const Wrapper = styled.div`
    display: inline-flex;
    flex-direction: column;
    position: relative;
    margin: 4px;
`;

export const Button = styled.button`
    align-items: center;
    appearance: none;
    background: var(--color-white);
    border-radius: 0;
    border: 1px solid var(--gray300);
    color: var(--gray700);
    cursor: pointer;
    display: inline-flex;
    font-size: 14px;
    justify-content: space-between;
    line-height: 1.25em;
    min-height: 32px;
    min-width: 48px;
    outline: none;
    padding: 4px 8px;
    text-align: left;
    width: auto;

    /* HOVER */
    :hover {
        border-color: var(--gray700);
        background: var(--gray50);
    }

    /* ACTIVE */
    ${(props) =>
        props.isOpen &&
        css`
            color: var(--blue500);
            border-color: var(--blue500);
        `}

    /* FOCUS */
    ${(props) =>
        props.isFocusVisible &&
        css`
            box-shadow: var(--outline);
        `}
    
    /* SELECTED */
    ${(props) => {
        return (
            props.isSelected &&
            css`
                background: var(--blue50);
                border-color: var(--blue500);
                color: var(--gray800);

                /* SELECTED HOVER */
                &:hover {
                    background: var(--gray50);
                    border-color: var(--myzanichelli-blue-dark);
                }

                /* SELECTED ACTIVE*/
                ${(props) =>
                    props.isOpen &&
                    css`
                        background: var(--color-white);
                        color: var(--blue500);
                    `}
            `
        );
    }}

    /* DISABLED */
    :disabled {
        background: var(--color-white);
        border-color: var(--gray200);
        color: #95999d;
        pointer-events: none;
    }
`;

export const Icon = styled(ZIcon)`
    margin-left: 8px;
    fill: var(--gray700);

    ${Button}:disabled & {
        fill: var(--gray200);
    }
`;

export const Value = styled.span`
    display: inline-flex;
    align-items: center;
`;

const Picker = (props) => {
    const ref = React.useRef(null);
    // Use components `props` to manage component state via `useSelectState`
    const state = useSelectState(props);

    // Get props for child elements from useSelect
    const { labelProps, triggerProps, valueProps, menuProps } = useSelect(props, state, ref);

    // Get props for the button based on the trigger props from useSelect
    const { buttonProps } = useButton(triggerProps, ref);

    // Handle focus
    const { focusProps, isFocusVisible } = useFocusRing();

    const { isDisabled, label } = props;
    const { close, isOpen, selectedItem } = state;

    return (
        <Wrapper>
            {label && (
                <VisuallyHidden>
                    <label {...labelProps}>{label}</label>
                </VisuallyHidden>
            )}
            {isDisabled ? (
                <Button disabled={true} aria-haspopup="listbox" aria-expanded="false">
                    <Value {...valueProps} key={selectedItem?.key | null}>
                        {selectedItem ? selectedItem.rendered : '...'}
                    </Value>
                    <Icon name={isOpen ? 'caret-up-filled' : 'caret-down-filled'}></Icon>
                </Button>
            ) : (
                <Button
                    {...mergeProps(buttonProps, focusProps)}
                    ref={ref}
                    isOpen={isOpen}
                    isFocusVisible={isFocusVisible}
                    isSelected={!!selectedItem}
                >
                    <Value {...valueProps} key={selectedItem?.key | null}>
                        {selectedItem ? selectedItem.rendered : '...'}
                    </Value>
                    <Icon name={isOpen ? 'caret-up-filled' : 'caret-down-filled'}></Icon>
                </Button>
            )}

            {isOpen && (
                <Popover isOpen={isOpen} onClose={close} pickerRef={ref}>
                    <ListBox {...menuProps} state={state} />
                </Popover>
            )}
        </Wrapper>
    );
};

Picker.propTypes = {
    isDisabled: PropTypes.bool,
    isOpen: PropTypes.bool,
    label: PropTypes.string,
    onSelectionChange: PropTypes.func,
    selectedKey: PropTypes.number,
};

export default Picker;
